import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/joy/Typography';
import { CircularProgress, IconButton, Chip, Dropdown, MenuButton, Menu, MenuItem, Button } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import request from '../helpers/request';
import { uploadFile } from '../helpers/storage';
import TaskResultDrawerLoadingShell, {
  TASK_RESULT_DRAWER_CONTENT_HEIGHT,
} from './TaskResultDrawerLoadingShell';

// TaskResult components
import DocumentPreview from '../pages/TaskResult/DocumentPreview';
import EventLogsCard from '../pages/TaskResult/EventLogsCard';
import ExtractedFieldsCard from '../pages/TaskResult/ExtractedFieldsCard';
import ValidationsCard from '../pages/TaskResult/ValidationsCard';
import ReferencesCard from '../pages/TaskResult/ReferencesCard';
import { DeleteTaskModal, FeedbackModal } from '../pages/TaskResult/modals';
import { updateNestedObject } from '../pages/TaskResult/utils';

// Icons
import { ThumbUp, ThumbDown, SmartToy, RefreshRounded } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const TaskResultDrawer = ({
  taskId,
  result: initialResult,
  input: initialInput,
  isLoading,
  isOpen,
  onClose,
  onExited,
  onTaskDeleted,
  onTaskRerun,
}) => {
  const navigate = useNavigate();
  
  // Local state for result and input (allows editing)
  const [result, setResult] = useState(initialResult || {});
  const [input, setInput] = useState(initialInput);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [documentPage, setDocumentPage] = useState(1);
  const [invertColors, setInvertColors] = useState(false);
  const [updatedByUser, setUpdatedByUser] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingAgentNavigationUrl, setPendingAgentNavigationUrl] = useState(null);
  const [isNavigatingToAgent, setIsNavigatingToAgent] = useState(false);
  const isNavigatingToAgentRef = useRef(false);
  const [hoveredField, setHoveredField] = useState(null);
  const [shownFieldKey, setShownFieldKey] = useState(null);
  const [showTriggerId, setShowTriggerId] = useState(0);

  // Sync with props when they change
  useEffect(() => {
    if (initialResult) setResult(initialResult);
  }, [initialResult]);

  useEffect(() => {
    if (initialInput) setInput(initialInput);
  }, [initialInput]);

  // Derived state
  const getOutput = result?.output?.metadata ? result?.output?.metadata : result?.output;
  const getFields = getOutput?.documentFields;
  const getValidations = getOutput?.validation;
  const getValidationStats = (key) => getOutput?.extractionStats?.validation?.[key];
  const payload = input?.payload;
  const getDocument = input?.document;
  const getReferences = payload?.objectReference;
  const isEvaluation = input?.type?.toLowerCase() === 'evaluation';
  const fieldBboxes = getOutput?.fieldBboxes;

  // Derive document type from input
  const getDocumentType = useMemo(() => {
    const legacyType = input?.payload?.document?.type;
    if (legacyType) {
      if (legacyType === "application/pdf") return "pdf";
      const img = legacyType.match(/^image\/([a-zA-Z0-9]+)/);
      if (img) return img[1].toLowerCase();
    }
    if (typeof getDocument === "string") {
      if (getDocument.startsWith("data:")) {
        const m = getDocument.match(/^data:([^;]+);/);
        if (m) {
          if (m[1] === "application/pdf") return "pdf";
          const imgMatch = m[1].match(/^image\/([a-zA-Z0-9]+)/);
          if (imgMatch) return imgMatch[1].toLowerCase();
        }
      }
      const match = getDocument.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      return match ? match[1].toLowerCase() : undefined;
    }
    return undefined;
  }, [input?.payload?.document?.type, getDocument]);

  // Memoized document URL
  const documentUrl = useMemo(() => {
    if (!getDocument || !getDocumentType) return "";
    if (["pdf", "png", "jpeg", "jpg"].includes(getDocumentType)) {
      return getDocument;
    }
    return "";
  }, [getDocument, getDocumentType]);


  // Load updated by user
  useEffect(() => {
    const fetchUser = async () => {
      if (getOutput && getOutput.updated_by) {
        try {
          const res = await request.get(`/api/v1/users/${getOutput.updated_by}/`);
          setUpdatedByUser(res.data);
        } catch {
          setUpdatedByUser(null);
        }
      }
    };
    fetchUser();
  }, [getOutput]);

  // Action handlers
  const handleDeleteTask = async () => {
    try {
      await request.delete(`/api/v1/tools/task/${taskId}/`);
      toast.success("Task archived successfully!");
      setDeleteModalOpen(false);
      if (onTaskDeleted) onTaskDeleted();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task. Please try again.");
    }
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setResult((prev) => {
      const path = prev?.output?.metadata ? ["output", "metadata", "documentFields"] : ["output", "documentFields"];
      return updateNestedObject(prev, path.concat(name), value);
    });
  };

  const handleSave = async () => {
    try {
      const updatedData = { output: result.output };
      await request.patch(`/api/v1/tools/result/${result.id}/`, updatedData);
      setIsEditing(false);
      toast.success('Extracted fields updated successfully!');
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error('Failed to save task result. Please try again.');
    }
  };

  const handleLike = async () => {
    try {
      const updatedOutput = {
        ...result.output,
        feedback: { type: "like" },
      };
      await request.patch(`/api/v1/tools/result/${result.id}/`, { output: updatedOutput });
      setResult((prev) => ({ ...prev, output: updatedOutput }));
      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error("Failed to save feedback");
    }
  };

  const handleDislike = async ({ field, comment }) => {
    try {
      const updatedOutput = {
        ...result.output,
        feedback: { type: "dislike", field, comment },
      };
      await request.patch(`/api/v1/tools/result/${result.id}/`, { output: updatedOutput });
      setResult((prev) => ({ ...prev, output: updatedOutput }));
      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error("Failed to save feedback");
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const taskRes = await request.get(`/api/v1/tools/task/${taskId}/`);
      setInput(prev => ({ ...taskRes.data, document: prev?.document }));
    } catch (err) {
      console.error('Failed to refresh task result:', err);
      toast.error('Failed to refresh task result');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleShowField = (fieldKey) => {
    const bboxData = fieldBboxes?.[fieldKey];
    if (!bboxData?.bbox) return;

    const pageNumber = Number(bboxData.page);
    if (Number.isFinite(pageNumber) && pageNumber > 0) {
      setDocumentPage(pageNumber);
    }

    setShownFieldKey(fieldKey);
    setShowTriggerId((prev) => prev + 1);
  };

  const handleExportJson = async () => {
    try {
      const response = await request.post(
        "api/v1/tools/export_task_result/",
        { task_id: result.task, format: "json" },
        { responseType: "json" }
      );

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `task_${result.task}_result.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Exported as JSON!");
    } catch (error) {
      console.error("JSON export failed", error);
      toast.error("Failed to export JSON");
    }
  };

  const handleGoToAgentChat = () => {
    if (isNavigatingToAgentRef.current) return;

    const policyId = input?.payload?.policy_id;
    const params = new URLSearchParams();
    params.set('taskId', taskId);
    if (policyId) params.set('policyId', policyId);
    const destination = `/app/agents?${params.toString()}`;

    if (!onClose) {
      navigate(destination);
      return;
    }

    isNavigatingToAgentRef.current = true;
    setPendingAgentNavigationUrl(destination);
    setIsNavigatingToAgent(true);
    onClose();
  };

  const handleExitComplete = () => {
    if (pendingAgentNavigationUrl) {
      const destination = pendingAgentNavigationUrl;
      setPendingAgentNavigationUrl(null);
      setIsNavigatingToAgent(false);
      isNavigatingToAgentRef.current = false;
      navigate(destination);
    }

    if (onExited) onExited();
  };

  const handleAddToEvaluation = async () => {
    try {
      const policyId = input?.payload?.policy_id;
      if (!policyId) {
        toast.error("No policy ID found for this task.");
        return;
      }

      const policyRes = await request.get(`/api/v1/compliance/policy/${policyId}/`);
      const compliancePolicy = policyRes.data;
      const policyName = compliancePolicy.name;

      const searchRes = await request.get(`/api/v1/compliance/policy/?search=${encodeURIComponent(policyName)}`);
      const allPolicies = searchRes.data.results || searchRes.data;

      let evalPolicy = allPolicies.find((policy) =>
        policy.settings?.type?.toLowerCase() === 'evaluation' &&
        policy.name === policyName &&
        String(policy.settings?.linked_policy_id) === String(policyId)
      );

      if (!evalPolicy) {
        const newPolicyPayload = {
          name: policyName,
          settings: {
            type: "Evaluation",
            linked_policy_id: policyId,
            tool: compliancePolicy.settings?.tool,
          },
          policy: {
            documents: [],
          },
        };
        const createRes = await request.post('/api/v1/compliance/policy/', newPolicyPayload);
        evalPolicy = createRes.data;
        toast.success(`Created new evaluation policy: ${evalPolicy.name}`);
      }

      const docId = `doc-${Date.now()}`;
      const extension = getDocumentType === 'pdf' ? 'pdf' : getDocumentType || 'pdf';
      const docName = `Task-${taskId}.${extension}`;

      let s3Key = null;
      const docUrl = getDocument || input?.payload?.document?.base64 || input?.payload?.document;

      if (!docUrl) {
        toast.error("No document found for this task.");
        return;
      }

      try {
        let blob;
        if (docUrl.startsWith('data:')) {
          const response = await fetch(docUrl);
          blob = await response.blob();
        } else {
          const response = await fetch(docUrl);
          blob = await response.blob();
        }

        const file = new File([blob], docName, { type: blob.type });
        s3Key = `evaluations/${evalPolicy.id}/${docId}/${docName}`;

        toast.info("Uploading document to evaluation dataset...");
        await uploadFile(file, s3Key);
      } catch (uploadError) {
        console.error("Failed to upload document copy:", uploadError);
        if (docUrl.startsWith('data:')) {
          toast.error("Could not upload document and it is a data URL. Cannot add to evaluation.");
          return;
        }
        toast.warn("Could not upload a copy of the document. Linking original URL.");
      }

      const newDoc = {
        id: docId,
        name: docName,
        url: s3Key ? null : docUrl,
        path: s3Key,
        s3Key,
        documentFields: result.output.documentFields || {},
        validations: [],
      };

      const currentDocuments = evalPolicy.policy.documents || [];
      const updatedDocuments = [...currentDocuments, newDoc];

      const patchPayload = {
        policy: {
          ...evalPolicy.policy,
          documents: updatedDocuments,
        },
      };

      await request.patch(`/api/v1/compliance/policy/${evalPolicy.id}/`, patchPayload);
      toast.success("Successfully added task result to evaluation dataset!");
    } catch (error) {
      console.error("Error adding to evaluation:", error);
      toast.error("Failed to add to evaluation policy.");
    }
  };

  // Status color mapping
  const statusColor = {
    'created': 'neutral',
    'in progress': 'primary',
    'action required': 'warning',
    'completed': 'success',
    'failed': 'danger',
  };

  const drawerTransition = {
    duration: 0.3,
    ease: 'easeInOut',
    layout: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  };

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isOpen && (
        <motion.div
          layout
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={drawerTransition}
          style={{ overflow: 'hidden' }}
        >
          <Box
            sx={{
              bgcolor: 'background.surface',
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            {isLoading ? (
              <TaskResultDrawerLoadingShell />
            ) : (
              <>
                {/* Compact Header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography level="title-lg">
                      Task #{taskId}
                    </Typography>
                    {input?.status && (
                      <Chip
                        color={statusColor[input.status.toLowerCase()] || 'neutral'}
                        size="sm"
                      >
                        {input.status.toUpperCase()}
                      </Chip>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      variant="soft"
                      color="neutral"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      aria-label="Refresh task result"
                    >
                      {isRefreshing ? <CircularProgress size="sm" /> : <RefreshRounded fontSize="small" />}
                    </IconButton>
                    {!isEvaluation ? (
                      <Dropdown>
                        <MenuButton
                          slots={{ root: IconButton }}
                          slotProps={{
                            root: {
                              variant: getOutput?.feedback?.type === "like" ? "solid" : "soft",
                              color: getOutput?.feedback?.type === "like" ? "success" : "neutral",
                              size: "sm",
                              'aria-label': "Like options",
                            },
                          }}
                        >
                          <ThumbUp fontSize="small" />
                        </MenuButton>
                        <Menu>
                          <MenuItem onClick={handleLike}>Like Task</MenuItem>
                          <MenuItem onClick={handleAddToEvaluation}>Add to Evaluation</MenuItem>
                        </Menu>
                      </Dropdown>
                    ) : (
                      <IconButton
                        variant={getOutput?.feedback?.type === "like" ? "solid" : "soft"}
                        color={getOutput?.feedback?.type === "like" ? "success" : "neutral"}
                        size="sm"
                        onClick={handleLike}
                        aria-label="Like task"
                      >
                        <ThumbUp fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      variant={getOutput?.feedback?.type === "dislike" ? "solid" : "soft"}
                      color={getOutput?.feedback?.type === "dislike" ? "danger" : "neutral"}
                      size="sm"
                      onClick={() => setFeedbackModalOpen(true)}
                      aria-label="Dislike task"
                    >
                      <ThumbDown fontSize="small" />
                    </IconButton>
                    <IconButton
                      variant="soft"
                      color="neutral"
                      size="sm"
                      onClick={handleExportJson}
                      aria-label="Export JSON"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                    <Button
                      variant="soft"
                      color="neutral"
                      size="sm"
                      startDecorator={<SmartToy fontSize="small" />}
                      onClick={handleGoToAgentChat}
                      disabled={isNavigatingToAgent}
                      aria-label="Investigate with agent"
                    >
                      Investigate
                    </Button>
                  </Stack>
                </Stack>

                {/* Main Content - Two Column Layout */}
                <Box sx={{ maxHeight: TASK_RESULT_DRAWER_CONTENT_HEIGHT, overflow: 'hidden', overflowX: 'hidden' }}>
                  <Grid container spacing={2} sx={{ height: '100%', minWidth: 0 }}>
                    {/* Document Preview */}
                    <Grid size={{ lg: 6, md: 12 }} sx={{ height: TASK_RESULT_DRAWER_CONTENT_HEIGHT, overflow: 'hidden' }}>
                      <DocumentPreview
                        id={taskId}
                        documentUrl={documentUrl}
                        documentType={getDocumentType}
                        invertColors={invertColors}
                        onToggleInvert={() => setInvertColors(!invertColors)}
                        hasDocument={!!getDocument}
                        fieldBboxes={fieldBboxes}
                        documentPage={documentPage}
                        onPageChange={setDocumentPage}
                        hoveredField={hoveredField}
                        shownFieldKey={shownFieldKey}
                        showTriggerId={showTriggerId}
                      />
                    </Grid>

                    {/* Extracted Fields & Other Cards */}
                    <Grid size={{ lg: 6, md: 12 }} sx={{ height: TASK_RESULT_DRAWER_CONTENT_HEIGHT, overflow: 'auto', overflowX: 'hidden', minWidth: 0 }}>
                      <Stack spacing={2} sx={{ pr: 1, minWidth: 0, overflowX: 'hidden' }}>
                        {/* Event Logs (inline when in progress or failed) */}
                        {['in progress', 'failed', 'error'].includes(input?.status) && (
                          <EventLogsCard events={input?.events} />
                        )}

                        {/* References, Token Usage */}
                        {!isEvaluation && (
                          <ReferencesCard
                            id={taskId}
                            tokenUsage={getOutput?.token_usage}
                            references={getReferences}
                            originalTaskId={payload?.original_task_id}
                            policyOutputId={input?.payload?.policy_output_id}
                          />
                        )}

                        {/* Extracted Fields */}
                        <ExtractedFieldsCard
                          id={taskId}
                          getFields={getFields}
                          getOutput={getOutput}
                          result={result}
                          isEditing={isEditing}
                          onEditToggle={() => setIsEditing(true)}
                          onSave={handleSave}
                          onFieldChange={handleFieldChange}
                          setDocumentPage={setDocumentPage}
                          updatedByUser={updatedByUser}
                          groundTruth={getReferences}
                          isEvaluation={isEvaluation}
                          onFieldHover={setHoveredField}
                          fieldBboxes={fieldBboxes}
                          onShowField={handleShowField}
                        />

                        {/* Validations */}
                        <ValidationsCard
                          id={taskId}
                          validations={getValidations}
                          getValidationStats={getValidationStats}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Box>

          {/* Modals */}
          <DeleteTaskModal
            open={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            taskId={taskId}
            onConfirm={handleDeleteTask}
          />

          <FeedbackModal
            open={isFeedbackModalOpen}
            onClose={() => setFeedbackModalOpen(false)}
            fieldNames={getFields ? Object.keys(getFields) : []}
            onSubmit={handleDislike}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TaskResultDrawer;
